// Copyright (c) Move Closer 2022.

import { AnyObject, Injectable, mapModel } from '@movecloser/front-core'
import { MetaPropertyProperty } from 'vue-meta'

import { Identifier } from '../related'
import { logger } from '../../../support'

import { ISite, SiteData, SiteModel, SitePropertyTypes, SitesMap } from './data.contracts'
import { ISiteService } from './service.contracts'
import { Site } from './site.model'
import { siteAdapterMap } from './site.adapter'
import { ImageProps } from '../../../dsl/atoms/Image'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
@Injectable()
export class SiteService implements ISiteService {
  protected activeSiteId: Identifier = 1
  protected activeSite: SiteModel | undefined = undefined
  protected properties: AnyObject = {}
  protected defaultCurrency: string = 'PLN'

  protected sites: SiteModel[] = []

  public getActiveSite (): SiteModel {
    if (!this.activeSite) {
      throw new Error('[SiteService]: Missing site.')
    }

    return this.activeSite
  }

  public getActiveSiteAddress (): string {
    return this.getActiveSite().address
  }

  public getActiveSiteBasePath (): string {
    return this.getSiteBasePath(this.getActiveSite())
  }

  public getSiteBasePath (site: SiteModel): string {
    const baseUrlParts = [
      site.basePath,
      site.postfix
    ]

    const basePath = baseUrlParts
      .map(part => this.stripTrailingSlash(part))
      .filter(part => part !== '')
      .join('/')

    return basePath ? `/${basePath}` : ''
  }

  public getActiveSiteLocale (): string {
    const site = this.getActiveSite()

    return site.locale.toLowerCase()
  }

  public getActiveSiteLogo (variant: string = 'light'): ImageProps | undefined {
    const site = this.getActiveSite()

    if (!site) {
      return
    }

    return {
      src: site.logo[variant],
      alt: site.name || '',
      isLazy: false,
      ...(site.properties?.logoSize as unknown as {width: number; height: number})
    }
  }

  public getActiveSiteMeta (): MetaPropertyProperty[] {
    const activeSite: SiteModel = this.getActiveSite()
    const meta: MetaPropertyProperty[] = []

    if (activeSite.properties.twitterSite && typeof activeSite.properties.twitterSite === 'string') {
      meta.push({ property: 'twitter:site', content: activeSite.properties.twitterSite })
    }

    if (activeSite.properties.siteName && typeof activeSite.properties.siteName === 'string') {
      meta.push({ property: 'og:site_name', content: activeSite.properties.siteName })
    }

    return meta
  }

  public getActiveSiteUrls (withAbsoluteUrls?: boolean): Record<string, string> {
    const activeSite: SiteModel = this.getActiveSite()

    const urls: Record<string, string> = {
      terms: activeSite.properties.regulationsLocation as string ?? '',
      privacy: activeSite.properties.privacyPolicyLocation as string ?? '',
      returnOrder: activeSite.properties.returnOrder as string ?? ''
    }

    if (withAbsoluteUrls) {
      const basePath = this.getActiveSiteBasePath()

      for (const p in urls) {
        if (urls[p].indexOf('http') < 0) {
          urls[p] = basePath + urls[p]
        }
      }
    }

    return urls
  }

  public getSites (): SiteModel[] {
    return this.sites
  }

  public getSiteById (id: Identifier): SiteModel | undefined {
    return this.sites.find(s => s.id === id)
  }

  public getProperty (key: string): SitePropertyTypes {
    return this.properties[key]
  }

  public getRelativeUrl (url: string): string {
    const relativeUrl = url.replace(new RegExp('^' + this.getActiveSiteBasePath()), '')

    if (relativeUrl && relativeUrl[0] === '/') {
      return relativeUrl.slice(1)
    }

    return relativeUrl
  }

  public setActive (site: SiteModel): void {
    logger('🏷 SiteService.setActive()')
    this.activeSite = Site.hydrate<SiteData, ISite>(
      mapModel({ ...{ currency: this.defaultCurrency }, ...site }, siteAdapterMap)
    )
    this.activeSiteId = site.id

    logger(['this.activeSite:', this.activeSiteId])
    logger(['this.getActiveSite():', JSON.stringify(this.activeSite)])
  }

  public setProperty (key: string, payload: SitePropertyTypes): void {
    this.properties = {
      ...this.properties,
      [key]: payload
    }
  }

  public setSites (sites: SitesMap): void {
    for (const payload of Object.values(sites)) {
      this.sites.push(
        Site.hydrate<SiteData, ISite>(
          mapModel(payload, siteAdapterMap)
        )
      )
    }
  }

  private stripTrailingSlash (s: string) {
    let stripped = s

    if (s.length && s[s.length - 1] === '/') {
      stripped = stripped.slice(0, -1)
    }

    if (s.length && s[0] === '/') {
      stripped = stripped.slice(1)
    }

    return stripped
  }
}
